import { Component, createRef } from 'preact';
import * as THREE from 'three';
import { FOIL_VERTEX_SHADER, FOIL_FRAGMENT_SHADER } from '../utils/sorcery/foilShader.js';
import { FOIL_WHITE_ZONES } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS, paramUniformName } from '../utils/sorcery/foilParams.js';
import { buildFoilUniforms, foilPlaceholderTexture, getFoilMatcap, loadFoilTextures } from '../utils/sorcery/foilMaterial.js';
import { isPhoneLayout } from '../utils/platform.js';

// Premium WebGL foil overlay for a single card (prototype: card inspector).
// Sits absolutely inside the (CSS-tilted) card wrapper and renders the card
// texture + foil shader, so it tracks the card's 3D tilt for free. Stays
// transparent until the texture loads, so the DOM <img> underneath shows as
// a fallback (and if WebGL init throws, we render nothing and the <img>
// remains visible). Tilt values come in via the `tilt` prop each frame.
// Hard cap on concurrent per-card WebGL foil contexts across the whole app
// (hand + unit hands + combat + inspector + pack opening + deck hover). Bounds
// total live contexts so transform-riding foil surfaces can't blow past the
// browser's ~16-context limit. Budget headroom: the browser allows ~16 live
// contexts; we reserve the rest for the board scene (1) and the dice (1).
// Grids now render their foil through the shared CardFoilScene (one context),
// NOT per-card FoilCanvas, so the old 6-cap is no longer needed to leave room
// for a grid context. And 6 was starving the gameboard, where the player hand
// + every unit-hand badge + combat portraits all mount per-card FoilCanvas at
// once. Above the cap the later mounts (e.g. unit-hand badges, freshly-drawn
// foil hand cards) render a blank canvas over the flat DOM art = "foil art but
// no shader effect." 14 is the safe MAX: 14 + board(1) = 15, one slot under the
// browser's ~16 live-context ceiling (the dice modal is transient + early, never
// concurrent with a foil-heavy hand). A foil-heavy hand that needs MORE than
// this many simultaneous per-card contexts cannot be served by per-card canvases
// at all. That requires the shared-scene approach (CardGrid/CardFoilScene).
// Instances beyond the cap render an empty transparent canvas -> the DOM <img>
// underneath shows (no foil) until a slot frees.
const FOIL_CANVAS_MAX = 14;
// Every live FoilCanvas with a real WebGL context, in mount order (oldest
// first). Used both to enforce the cap and to evict the oldest NON-priority
// instance when a priority instance needs a slot but the budget is full. So
// the focused inspector/preview always renders while total contexts stay
// bounded. A priority instance still COUNTS against the budget (it occupies a
// slot); it just gets to claim one by force-disposing an older non-priority
// foil instead of being blocked.
const _foilCanvasLive = [];

export default class FoilCanvas extends Component {
  canvasRef = createRef();
  _tilt = { mx: 50, my: 50, fromCenter: 0 };

  componentDidMount() {
    if (this.props.tilt) this._tilt = this.props.tilt;
    try {
      this._init();
    } catch (err) {
      console.warn('[FoilCanvas] WebGL init failed, falling back to DOM image:', err?.message || err);
      this._dispose();
    }
  }

  componentDidUpdate() {
    if (this.props.tilt) this._tilt = this.props.tilt;
    this._applyParams();
  }

  _applyParams() {
    if (!this.uniforms) return;
    const p = this.props.params || DEFAULT_FOIL_PARAMS;
    for (const k of Object.keys(DEFAULT_FOIL_PARAMS)) {
      const u = this.uniforms[paramUniformName(k)];
      if (u) u.value = p[k];
    }
    this._dirty = true; // params (dev panel) changed -> repaint
  }

  componentWillUnmount() {
    this._dispose();
  }

  _init() {
    const canvas = this.canvasRef.current;
    // Context budget: every foil canvas counts against a single global ceiling
    // (FOIL_CANVAS_MAX) so the live total can't blow past the browser's
    // ~16-context limit (which would otherwise evict the shared FoilStage and
    // white-crash the grid). PRIORITY instances (focused inspector / hover
    // preview) still count. They are NOT uncapped. But when the budget is
    // full they claim a slot by force-disposing the OLDEST non-priority foil
    // (e.g. a deck-grid hover) so the focused surface always renders while the
    // total stays bounded. Non-priority instances that can't get a slot render
    // an empty transparent canvas -> the DOM <img> underneath shows.
    if (_foilCanvasLive.length >= FOIL_CANVAS_MAX) {
      if (!this.props.priority) { this._capped = true; return; }
      const victim = _foilCanvasLive.find((fc) => !fc.props.priority);
      if (victim) victim._evict();
      else { this._capped = true; return; } // budget full of priority instances
    }
    _foilCanvasLive.push(this);
    this._countsToward = true;
    this._hasContext = true;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, premultipliedAlpha: false });
    // Raw passthrough: the custom ShaderMaterial samples + writes the texture
    // directly, so we must NOT let Three apply sRGB decode/encode (it would
    // sample toward linear without re-encoding and the card would render
    // darker than the DOM <img>). NoColorSpace texture + LinearSRGB output =
    // the exact image bytes reach the canvas, matching the non-foil card.
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;

    // WebGL context-loss handling: when the browser drops the oldest context
    // (too many live contexts), preventDefault lets it RESTORE this one instead
    // of losing it permanently (which white-flashed + spewed cross-context
    // delete errors). Pause rendering while lost; re-upload textures on restore.
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this._lost = true; }, false);
    canvas.addEventListener('webglcontextrestored', () => {
      this._lost = false;
      this._dirty = true; // restored context -> repaint
      [this.uniforms?.uTex, this.uniforms?.uMatcap, this.uniforms?.uFoilMap, this.uniforms?.uDepthMap].forEach((u) => {
        if (u && u.value) u.value.needsUpdate = true;
      });
    }, false);

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const zones = this.props.zones || FOIL_WHITE_ZONES;
    this.uniforms = buildFoilUniforms({ zones });
    // Apply this instance's live params over the defaults baked in by the
    // helper (the dev panel tunes these via _applyParams).
    const p = this.props.params || DEFAULT_FOIL_PARAMS;
    for (const k of Object.keys(DEFAULT_FOIL_PARAMS)) {
      this.uniforms[paramUniformName(k)].value = p[k];
    }
    // Sites render the portrait texture rotated 90deg CW (see foilShader).
    this.uniforms.uRotate90.value = this.props.rotate90 ? 1 : 0;
    // Re-point uMatcap when the shared studio matcap finishes decoding.
    getFoilMatcap((tex) => { this.uniforms.uMatcap.value = tex; });
    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: FOIL_VERTEX_SHADER,
      fragmentShader: FOIL_FRAGMENT_SHADER,
      transparent: true,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    this.scene.add(this.mesh);

    // Card art (NoColorSpace, raw passthrough) + sibling baked foil map. The
    // foil map (RG=normal, B=height, A=white/foil mask) is prefetched to disk
    // by the asset gate; if it 404s the shader keeps its runtime-derived path
    // (uHasFoilMap stays 0).
    // Cap texture decode at what this canvas box can resolve (25% tilt/zoom
    // headroom, 256-bucketed). An unlaid-out box skips the cap.
    const boxLong = Math.max(canvas.clientWidth || 0, canvas.clientHeight || 0)
      * Math.min(window.devicePixelRatio || 1, isPhoneLayout() ? 1.5 : 3);
    loadFoilTextures(this.props.imageUrl, {
      texColorSpace: THREE.NoColorSpace,
      maxSize: boxLong > 0
        ? Math.min(1024, Math.max(256, Math.ceil((boxLong * 1.25) / 256) * 256))
        : undefined,
      onTex: (tex) => {
        this.uniforms.uTex.value = tex;
        if (tex.image) this.uniforms.uTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
        this._ready = true;
        this._dirty = true; // card art just became drawable
        this._applyObjectCover(); // crop now that the texture aspect is known
      },
      onFoilMap: (tex) => { this.uniforms.uFoilMap.value = tex; this.uniforms.uHasFoilMap.value = 1; this._dirty = true; },
      onDepthMap: (tex) => { this.uniforms.uDepthMap.value = tex; this.uniforms.uHasDepthMap.value = 1; this._dirty = true; },
    });

    this._ro = new ResizeObserver(() => this._resize());
    this._ro.observe(canvas);
    this._resize();

    this._last = 0;
    this._lastTilt = null;
    this._loop = (t) => {
      this._raf = requestAnimationFrame(this._loop);
      if (!this._ready || this._lost) return;
      const dt = this._last ? (t - this._last) / 1000 : 0;
      this._last = t;
      this.uniforms.uTime.value += dt; // unused by the shader; kept for parity
      if (this.props.scrollSweep) {
        // Deck-builder sweep: drive tilt from the card's position in the
        // viewport so scrolling slides the holo across the grid (light stays
        // fixed, cards move past it). Position changes every frame as the page
        // scrolls, so this path ALWAYS renders. One rect read/frame. Cheap.
        const r = canvas.getBoundingClientRect();
        const cx = (r.left + r.width / 2) / (window.innerWidth || 1);
        const cy = (r.top + r.height / 2) / (window.innerHeight || 1);
        this.uniforms.uTilt.value.set(cx - 0.5, cy - 0.5);
        this.uniforms.uFromCenter.value = Math.min(1, Math.hypot(cx - 0.5, cy - 0.5) * 2);
      } else {
        // Gate redraws: uTime is unused by the shader, so an idle frame with the
        // same tilt is pixel-identical to the last. Render only when the tilt
        // prop changed, an input went dirty (texture/params/resize/restore), or
        // the first frame hasn't fired onCover yet. Keep the rAF alive so a later
        // tilt change resumes instantly.
        const tilt = this._tilt;
        const lt = this._lastTilt;
        const tiltChanged = !lt || tilt.mx !== lt.mx || tilt.my !== lt.my || (tilt.fromCenter || 0) !== lt.fromCenter;
        if (this._covered && !this._dirty && !tiltChanged) return;
        this.uniforms.uTilt.value.set((tilt.mx / 100) - 0.5, (tilt.my / 100) - 0.5);
        this.uniforms.uFromCenter.value = tilt.fromCenter || 0;
        this._lastTilt = { mx: tilt.mx, my: tilt.my, fromCenter: tilt.fromCenter || 0 };
      }
      this.renderer.render(this.scene, this.camera);
      this._dirty = false;
      // First real frame on screen: tell the parent the foil now covers the
      // card so it can hide the underlying DOM <img> (prevents the plain,
      // mask-less image bleeding through in 3D-transform contexts like the
      // pack-opening fan). Capped instances never reach here, so the parent
      // keeps the <img> as the fallback.
      if (!this._covered) { this._covered = true; this.props.onCover?.(true); }
    };
    this._raf = requestAnimationFrame(this._loop);
  }

  _resize() {
    const canvas = this.canvasRef.current;
    if (!canvas || !this.renderer) return;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    // Buffer = device pixels, floored at 2 and capped at 3. Floor 2: a DPR-1
    // Windows display rendered 1x looks blurry next to the browser-super-sampled
    // DOM <img>. Cap 3 (not 2): a 4K Windows screen at 200%+ scaling has DPR
    // 2.5-3, and capping at 2 made the foil buffer SMALLER than the physical
    // screen -> blurry while the <img> stayed crisp. These are small per-card
    // canvases, so 3x is cheap (unlike the grid's full-viewport scene canvas).
    // Phones cap at 1.5 instead: no super-sample payoff at card size, and the
    // buffer counts against iOS Safari's per-tab memory budget.
    this.renderer.setPixelRatio(isPhoneLayout()
      ? Math.min(window.devicePixelRatio || 1, 1.5)
      : Math.min(Math.max(window.devicePixelRatio || 1, 2), 3));
    this.renderer.setSize(w, h, false);
    this._applyObjectCover(); // quad aspect may have changed -> recrop
    this._dirty = true; // size/crop changed -> repaint
  }

  // Object-cover crop (matches the DOM <img> object-cover + the shared
  // FoilStage): compare the card-art texture aspect to the canvas's quad
  // aspect, crop the over-long axis and center it via uUvScale/uUvOffset. This
  // is what keeps a landscape-art SITE aspect-correct inside its quad. At rest
  // AND through the 90deg hover pivot (the canvas rides the CSS transform, so
  // its local client box, and thus this crop, stays valid as it rotates).
  // Portrait card art in a portrait quad gets ~1,1 / 0,0 (a no-op), so every
  // existing non-site foil surface is byte-identical to before.
  _applyObjectCover() {
    const canvas = this.canvasRef.current;
    const img = this.uniforms?.uTex?.value?.image;
    if (!canvas || !img || !img.width || !img.height) return;
    // Rotated sites: the rotated portrait aspect already matches the landscape
    // quad (~1.40 vs ~1.40), so the cover crop is identity. The unrotated crop
    // below would compute a bogus vertical crop from the portrait aspect.
    if (this.props.rotate90) {
      this.uniforms.uUvScale.value.set(1, 1);
      this.uniforms.uUvOffset.value.set(0, 0);
      return;
    }
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    const texAspect = img.width / img.height;
    const quadAspect = w / h;
    let usX = 1, usY = 1, uoX = 0, uoY = 0;
    if (texAspect > quadAspect) {
      usX = quadAspect / texAspect;
      uoX = (1 - usX) / 2;
    } else if (texAspect < quadAspect) {
      usY = texAspect / quadAspect;
      uoY = (1 - usY) / 2;
    }
    this.uniforms.uUvScale.value.set(usX, usY);
    this.uniforms.uUvOffset.value.set(uoX, uoY);
  }

  // Force-dispose this instance's WebGL context to free a budget slot for a
  // higher-priority foil (called from another instance's _init). The component
  // stays mounted but falls back to the DOM <img>: we drop the context, leave
  // _capped set so it isn't counted as live, and tell the parent to re-show the
  // image. A later mount/update won't resurrect it (no re-init path), which is
  // fine. Eviction targets transient grid-hover foils that unmount on leave.
  _evict() {
    this._capped = true;
    this._dispose();
  }

  _dispose() {
    if (this._covered) { this._covered = false; this.props.onCover?.(false); }
    if (this._countsToward) {
      const i = _foilCanvasLive.indexOf(this);
      if (i !== -1) _foilCanvasLive.splice(i, 1);
      this._countsToward = false;
    }
    this._hasContext = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._ro?.disconnect();
    // Only dispose textures this instance owns. The matcap is shared
    // app-wide (foilMaterial.js) and uFoilMap may be the shared placeholder.
    // disposing either would break other live foils.
    const placeholder = foilPlaceholderTexture();
    this.uniforms?.uTex?.value?.dispose?.();
    const foilMap = this.uniforms?.uFoilMap?.value;
    if (foilMap && foilMap !== placeholder) foilMap.dispose?.();
    const depthMap = this.uniforms?.uDepthMap?.value;
    if (depthMap && depthMap !== placeholder) depthMap.dispose?.();
    this.mesh?.geometry?.dispose();
    this.material?.dispose();
    // dispose() frees Three's tracked GL objects but leaves the underlying
    // WebGL context alive until GC. Rapid hover mount/unmount would pile up
    // zombie contexts, hit the browser's ~16-context ceiling, and get the
    // oldest live context (the grid) silently killed -> cross-context delete
    // errors. forceContextLoss releases this context immediately.
    this.renderer?.dispose();
    this.renderer?.forceContextLoss?.();
    this.renderer = null;
  }

  render() {
    return (
      <canvas
        ref={this.canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 'inherit', pointerEvents: 'none', zIndex: 5 }}
      />
    );
  }
}
